// HQ Circulars and Gallery listings from the FastAPI backend (Google Drive).
// Credentials stay on Render. This file never sees a service-account key.

import { apiUrl, authHeaders, hasApi, NO_BACKEND_MESSAGE, SESSION_EXPIRED_MESSAGE, clearClientSession, isAuthFailure } from "./api";

export const CIRCULAR_CATEGORIES = [
  { value: "circular", label: "Circulars", type: "Circular", folder: "Circulars" },
  { value: "gos", label: "GOs", type: "Order", folder: "Circulars / GOs" },
  { value: "notifications", label: "Notifications", type: "Notification", folder: "Circulars / Notifications" },
];

export const GALLERY_ALBUMS = [
  { value: "events", label: "Events", category: "Milestones & Events", folder: "Gallery / Events", folderName: "Events" },
  { value: "eluru", label: "Eluru", category: "GMC Eluru", folder: "Gallery / Eluru", folderName: "Eluru" },
  { value: "machilipatnam", label: "Machilipatnam", category: "GMC Machilipatnam", folder: "Gallery / Machilipatnam", folderName: "Machilipatnam" },
  { value: "nandyal", label: "Nandyal", category: "GMC Nandyal", folder: "Gallery / Nandyal", folderName: "Nandyal" },
  { value: "rajamahendravaram", label: "Rajamahendravaram", category: "GMC Rajamahendravaram", folder: "Gallery / Rajamahendravaram", folderName: "Rajamahendravaram" },
  { value: "vizianagaram", label: "Vizianagaram", category: "GMC Vizianagaram", folder: "Gallery / Vizianagaram", folderName: "Vizianagaram" },
  { value: "paderu", label: "Paderu", category: "GMC Paderu", folder: "Gallery / Paderu", folderName: "Paderu" },
  { value: "piduguralla", label: "Piduguralla", category: "GMC Piduguralla", folder: "Gallery / Piduguralla", folderName: "Piduguralla" },
];

export const GALLERY_COLLEGE_ALBUMS = GALLERY_ALBUMS.filter((a) => a.value !== "events");

export const CAREERS_FOLDER = "Careers";

export const DRIVE_NOT_CONNECTED = "Drive is not connected";
export const MAX_GALLERY_BYTES = 8 * 1024 * 1024;
export const MAX_GALLERY_LABEL = "8 MB";
export const UPLOAD_TIMEOUT_MS = 28000;
export const API_HEALTH_URL = "https://apmercl-backend.onrender.com/health";
export const RENDER_RECOVERY_NOTE =
  `The API is on a paid Render Starter (it should not sleep). Open ${API_HEALTH_URL}. If that is down, ask Tech to Manual Deploy the latest commit on Starter — do not upgrade the plan again. Keep photos under ${MAX_GALLERY_LABEL}.`;
export const RENDER_SLEEP_NOTE = RENDER_RECOVERY_NOTE;

const empty = (error) => ({ connected: false, source: "none", error, files: [] });

export function apiDetail(payload, fallback = "") {
  const raw = payload && payload.detail;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (Array.isArray(raw) && raw.length) {
    const parts = raw.map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item.msg === "string") return item.msg;
      return "";
    }).filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  if (typeof payload?.error === "string" && payload.error.trim()) return payload.error.trim();
  return fallback;
}

export function networkErrorMessage(err) {
  const name = err && err.name;
  if (name === "AbortError") {
    return `The server timed out (Render stops uploads after 30 seconds). Use a smaller JPG (under ${MAX_GALLERY_LABEL}) and try again. ${RENDER_RECOVERY_NOTE}`;
  }
  return `Could not reach the APMERCL server. ${RENDER_RECOVERY_NOTE}`;
}

export function statusMessage(status, detail) {
  if (isAuthFailure(status, detail)) return SESSION_EXPIRED_MESSAGE;
  if (status === 403) return detail || "You do not have permission to upload.";
  if (status === 404) {
    return "This upload service is not on the live server yet. Ask Tech to redeploy the API on Render.";
  }
  if (status === 413) return detail || `That file is larger than ${MAX_GALLERY_LABEL}.`;
  if (status === 422) return detail || "The upload was missing the photograph or the Events / college folder.";
  if (status === 502 || status === 503 || status === 504) {
    if (detail && detail !== "Not Found") return detail;
    return `The APMERCL server did not finish. ${RENDER_RECOVERY_NOTE}`;
  }
  return detail || DRIVE_NOT_CONNECTED;
}

export function isTransientServerError(text) {
  const t = String(text || "");
  return /Could not reach|timed out|sleep when idle|Manual Deploy|not on the live server yet|did not finish|do not upgrade/i.test(t);
}

/** Name tag under a gallery photo. A Drive file with no title uses its file name. */
export function galleryNameTag(file) {
  const titled = String((file && file.title) || "").trim();
  if (titled) return titled;
  const raw = String((file && file.name) || "").trim();
  const base = raw.replace(/\.[^.]+$/, "").trim();
  if (!base) return raw || "Untitled";
  return base.includes(" ") ? base : (base.replace(/[_-]+/g, " ").trim() || base);
}

/**
 * Drive photographs first, then other sources. Two different Drive files both stay.
 * A built-in picture with the same name tag as a Drive file does not show twice.
 */
export function mergeGalleryItems(groups) {
  const seenIds = new Set();
  const seenTitles = new Set();
  const merged = [];
  for (const item of groups) {
    const title = galleryNameTag(item);
    const category = String(item.category || "");
    const titleKey = `${category.toLowerCase()}|${title.toLowerCase().replace(/[—–]/g, "-")}`;
    const id = item && item.id ? String(item.id) : "";
    if (id) {
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      seenTitles.add(titleKey);
      merged.push({ ...item, title });
      continue;
    }
    if (seenTitles.has(titleKey)) continue;
    seenTitles.add(titleKey);
    merged.push({ ...item, title });
  }
  return merged;
}

async function getJson(path) {
  const url = apiUrl(path);
  if (!url) return empty(NO_BACKEND_MESSAGE);
  try {
    const r = await fetch(url, { cache: "no-store" });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      // 401/403/404 mean the API answered. Only fetch() failures are "unreachable".
      return empty(statusMessage(r.status, apiDetail(d, "")));
    }
    return {
      connected: d.connected === true,
      source: d.source || (d.connected ? "drive" : "none"),
      error: d.error || (d.connected ? null : DRIVE_NOT_CONNECTED),
      files: Array.isArray(d.files) ? d.files : [],
      folders: d.folders || null,
    };
  } catch (err) {
    return empty(networkErrorMessage(err));
  }
}

export async function fetchSiteCirculars() {
  return getJson("/api/circulars");
}

export async function fetchSiteGallery() {
  return getJson("/api/gallery");
}

export async function syncWebsiteGalleryPhotos() {
  const url = apiUrl("/api/gallery/sync-website");
  if (!url) return { ok: false, status: 0, detail: NO_BACKEND_MESSAGE };
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), UPLOAD_TIMEOUT_MS) : null;
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      signal: ctrl ? ctrl.signal : undefined,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (r.status === 401) clearClientSession();
      return { ok: false, status: r.status, detail: statusMessage(r.status, apiDetail(d, "")), ...d };
    }
    return { ok: true, status: r.status, ...d };
  } catch (err) {
    return { ok: false, status: 0, detail: networkErrorMessage(err) };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function fetchSiteCareers() {
  return getJson("/api/careers");
}

export async function fetchDriveStatus() {
  const url = apiUrl("/api/drive/status");
  if (!url) return { configured: false, connected: false, error: NO_BACKEND_MESSAGE };
  try {
    const r = await fetch(url);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      return { configured: false, connected: false, error: statusMessage(r.status, apiDetail(d, "")) };
    }
    return {
      configured: d.configured === true || d.connected === true,
      connected: d.connected === true || d.configured === true,
      error: d.error || null,
      folder: d.folder || "",
      folders: d.folders || null,
    };
  } catch (err) {
    return { configured: false, connected: false, error: networkErrorMessage(err) };
  }
}

export async function uploadSiteFile(path, form) {
  const url = apiUrl(path);
  if (!url) return { ok: false, status: 0, detail: NO_BACKEND_MESSAGE };
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), UPLOAD_TIMEOUT_MS) : null;
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: authHeaders(),
      body: form,
      credentials: "include",
      signal: ctrl ? ctrl.signal : undefined,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (r.status === 401) clearClientSession();
      return { ok: false, status: r.status, detail: statusMessage(r.status, apiDetail(d, "")) };
    }
    return { ok: true, status: r.status, file: d };
  } catch (err) {
    return { ok: false, status: 0, detail: networkErrorMessage(err) };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function deleteSiteFile(path) {
  const url = apiUrl(path);
  if (!url) return { ok: false, status: 0, detail: NO_BACKEND_MESSAGE };
  try {
    const r = await fetch(url, { method: "DELETE", headers: authHeaders(), credentials: "include" });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (r.status === 401) clearClientSession();
      return { ok: false, status: r.status, detail: statusMessage(r.status, apiDetail(d, "")) };
    }
    return { ok: true, status: r.status };
  } catch (err) {
    return { ok: false, status: 0, detail: networkErrorMessage(err) };
  }
}

export function hasApiBackend() {
  return hasApi();
}

export function isGalleryImageFile(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name || "");
}
